import { DateTime } from 'luxon';

import { setCurrentCase } from './slices/casesSlice';

import { clearAccounts } from './slices/accountsSlice';
import { clearAlerts } from './slices/alertsSlice';
import { clearApiResponseInfo } from './slices/apiResponseInfoSlice';
import { clearCampaigns } from './slices/campaignsSlice';
import { clearCases } from './slices/casesSlice';
import { clearClientsInfo } from './slices/clientsInfoSlice';
import { clearContactInfo } from './slices/contactInfoSlice';
import { clearCrCalls } from './slices/callRailCallsSlice';
import { clearDashboardStats } from './slices/dashboardStatsSlice';
import { clearDistributionLists } from './slices/distributionListsSlice';
import { clearDocuments } from './slices/documentsSlice';
import { clearEmergencyContacts } from './slices/emergencyContactsSlice';
import { clearEmrInfo, setEmrFacilities } from './slices/emrInfoSlice';
import { clearFacilities } from './slices/facilitiesSlice';
import { clearFilterStates } from './slices/filterStatesSlice';
import { clearFirmInfo } from './slices/firmInfoSlice';
import { clearFirms } from './slices/firmsSlice';
import { clearInquiries } from './slices/inquiriesSlice';
import { clearInsuranceInfos } from './slices/insuranceInfosSlice';
import { clearIntendedInfo } from './slices/intendedInfoSlice';
import { clearLogins } from './slices/loginsSlice';
import { clearMisc } from './slices/miscSlice';
import { clearMissives } from './slices/missivesSlice';
import { clearNotes } from './slices/notesSlice';
import { clearOrgContacts } from './slices/orgContactsSlice';
import { clearOrgTasks } from './slices/orgTasksSlice';
import { clearOutreaches2 } from './slices/outreachesSlice';
import { clearOutreaches } from './slices/outreachListsSlice';
import { clearPersons } from './slices/personsSlice';
import { clearReferrals } from './slices/referralsSlice';
import { clearThinReferrals } from './slices/thinReferralsSlice';
import { clearStatusTransitions } from './slices/statusTransitionsSlice';
import { clearTaskInfo } from './slices/taskInfoSlice';
import { clearTreatmentPrograms } from './slices/treatmentProgramsSlice';
import { clearUserInfo } from './slices/userInfoSlice';
import { clearUserSettings } from './slices/userSettingsSlice';
import { clearVobFormInfos } from './slices/vobFormInfosSlice';
import { clearVobResolutions } from './slices/vobResolutionsSlice';

import { clearLocalStorage } from './reduxPersist';

import ReduxLoader from './ReduxLoader';

class ReduxAccess {
  store = undefined;

  clearStore(includingLoginInfo = false) {
    this.store.dispatch(clearAccounts());
    this.store.dispatch(clearAlerts());
    this.store.dispatch(clearApiResponseInfo());
    this.store.dispatch(clearCampaigns());
    this.store.dispatch(clearCases());
    this.store.dispatch(clearClientsInfo());
    this.store.dispatch(clearContactInfo());
    this.store.dispatch(clearCrCalls());
    this.store.dispatch(clearDashboardStats());
    this.store.dispatch(clearDistributionLists());
    this.store.dispatch(clearDocuments());
    this.store.dispatch(clearEmergencyContacts());
    this.store.dispatch(clearEmrInfo());
    this.store.dispatch(clearFacilities());
    this.store.dispatch(clearFilterStates());
    this.store.dispatch(clearFirmInfo());
    this.store.dispatch(clearFirms());
    this.store.dispatch(clearInquiries());
    this.store.dispatch(clearInsuranceInfos());
    this.store.dispatch(clearIntendedInfo());
    // don't do this next one, so we can log in as previous (SuperAdmin) user
    // this.store.dispatch(clearLogins());
    this.store.dispatch(clearMisc());
    this.store.dispatch(clearMissives());
    this.store.dispatch(clearNotes());
    this.store.dispatch(clearOrgContacts());
    this.store.dispatch(clearOrgTasks());
    this.store.dispatch(clearOutreaches());
    this.store.dispatch(clearOutreaches2());
    this.store.dispatch(clearPersons());
    this.store.dispatch(clearReferrals());
    this.store.dispatch(clearThinReferrals());
    this.store.dispatch(clearStatusTransitions());
    this.store.dispatch(clearTaskInfo());
    this.store.dispatch(clearTreatmentPrograms());
    this.store.dispatch(clearUserInfo());
    this.store.dispatch(clearUserSettings());
    this.store.dispatch(clearVobFormInfos());
    this.store.dispatch(clearVobResolutions());

    if (includingLoginInfo) {
      this.store.dispatch(clearLogins());
    }

    ReduxLoader.clear();
    clearLocalStorage();
    this.cachedFirmId = undefined;
  }

  setStore(aStore) {
    this.store = aStore;
  }

  getFirmId() {
    if (!this.cachedFirmId) {
      this.cachedFirmId = this.store.getState().firmInfo.firmId;
    }
    return this.cachedFirmId;
  }

  logStore() {
    // console.log(this.store.getState());
  }

  getTopLogin() {
    const state = this.store.getState();
    const logins = state.logins;
    return logins.length === 0 ? null : logins[0];
  }

  getLoggedInUserPersonId() {
    const state = this.store.getState();
    return state.userInfo ? state.userInfo.personId : 0;
  }

  haveLoggedInUser() {
    return this.getLoggedInUserPersonId() !== 0;
  }

  getLoggedInUserEmail() {
    const state = this.store.getState();
    return state.userInfo.email;
  }

  getLoggedInUserId() {
    const state = this.store.getState();
    return state.userInfo.userId;
  }

  getLoggedInUserInfo() {
    if (this.store) {
      const state = this.store.getState();
      return state.userInfo;
    } else {
      return null;
    }
  }

  getAllLoggedInUserSettings() {
    if (this.store) {
      return this.store.getState().userSettings;
    } else {
      return null;
    }
  }

  getUserSetting(settingName) {
    if (this.store) {
      const settings = this.store.getState().userSettings[settingName];
      return settings || [];
    } else {
      return [];
    }
  }

  userWasLoggedOutDueToIdleTimeout() {
    if (this.store) {
      const miscState = this.store.getState().misc;
      return miscState.loggedOutDueToIdleTimeout;
    } else {
      return false;
    }
  }

  getLoggedInFirmInfo() {
    if (this.store) {
      const state = this.store.getState();
      return state.firmInfo;
    } else {
      return null;
    }
  }

  getAllFirmUsers() {
    const myStore = this.store;
    if (myStore) {
      const { adminsIdsNamesAndEmails, usersIdsNamesAndEmails } = myStore.getState().firmInfo;
      if (adminsIdsNamesAndEmails.length > 0) {
        return [...adminsIdsNamesAndEmails, ...usersIdsNamesAndEmails];
      } else {
        return [];
      }
    } else {
      return [];
    }
  }

  getUnaffiliatedContacts() {
    const myState = this.store.getState();
    return myState.firmInfo.unaffiliatedContacts;
  }

  getAllCampaigns() {
    const myStore = this.store;
    if (myStore) {
      return myStore.getState().campaigns.map((c) => this.resolveCampaignDates(c));
    } else {
      return [];
    }
  }

  resolveCampaignDates(campaign) {
    const { startDate, endDate } = campaign;
    const luxonEndDate = endDate.startsWith('9999') ? null : DateTime.fromISO(endDate);
    return {
      ...campaign,
      startDate: DateTime.fromISO(startDate),
      endDate: luxonEndDate,
    };
  }

  getCampaignWithId(campaignId) {
    const campaign = this.store.getState().campaigns.find((c) => c.campaignId === campaignId);
    return this.resolveCampaignDates(campaign);
  }

  getUserByEmail(email) {
    const lowerCaseEmail = email.toLowerCase();
    const allUsers = this.getAllFirmUsers();
    return allUsers.find((user) => user.email.toLowerCase() === lowerCaseEmail);
  }

  getInfoForUser(userId) {
    const allUsers = this.getAllFirmUsers();
    return allUsers.find((user) => user.id === userId);
  }

  getRoleOfUserWithId(userId) {
    const { adminsIdsNamesAndEmails, usersIdsNamesAndEmails } = this.store.getState().firmInfo;
    if (adminsIdsNamesAndEmails.some((u) => u.id === userId)) {
      return 'Admin';
    } else if (usersIdsNamesAndEmails.some((u) => u.id === userId)) {
      return 'User';
    } else {
      return 'Unknown';
    }
  }

  getPersonById(personId) {
    const personsState = this.store.getState().persons;
    const person = personsState[personId];
    if (person) {
      return this.resolvePersonDatesToDateTimes(person);
    } else {
      console.log(`ReduxAccess: Couldn't find Person with ID: ${personId}`);
      return undefined;
    }
  }

  resolvePersonDatesToDateTimes(person) {
    return {
      ...person,
      birthDate: person.birthDate ? DateTime.fromISO(person.birthDate) : null,
      createdDate: person.createdDate ? DateTime.fromISO(person.createdDate) : null,
      latestContactDate: person.latestContactDate ? DateTime.fromISO(person.latestContactDate) : null,
    };
  }

  resolvePersonDatesToIso(person) {
    return {
      ...person,
      birthDate: person.birthDate ? person.birthDate.toISO() : null,
      createdDate: person.createdDate ? person.createdDate.toISO() : null,
      latestContactDate: person.latestContactDate ? person.latestContactDate.toISO() : null,
    };
  }

  getEmrProspectDetail(personId) {
    const reduxState = this.store.getState();
    if (!reduxState.emrInfo.isSyncedWithFirm) {
      // MKM: Remove diagnostic logging when we've fixed the 'disappearing facilities' issue
      const showDiagnosticLogging = false;
      if (showDiagnosticLogging) {
        console.log(
          `Returning null from getEmrProspectDetail(${personId}) because state.emrInfo.isSyncedWithFirm is falsey`
        );
      }
      return null;
    }
    const emrClients = reduxState.emrInfo.clients;
    const emrClient = emrClients.find((emrC) => emrC.personId === personId);

    // MKM: Remove diagnostic logging when we've fixed the 'disappearing facilities' issue
    if (!emrClient) {
      const nClients = emrClients.length;
      const msgPt1 = `Returning something falsey from getEmrProspectDetail(${personId}) because`;
      const msgPt2 = `reduxState.emrInfo.clients (n=${nClients}) didn't have an element with personId === ${personId}`;
      console.log(`${msgPt1} ${msgPt2}`);
    }

    return emrClient;
  }

  getAllEmrFacilities() {
    const emrInfo = this.store.getState().emrInfo;
    const reduxFacilities = emrInfo.facilities;
    if (emrInfo.isSyncedWithFirm && reduxFacilities.length == 0) {
      return this.buildFacilitiesListFromClientsList(emrInfo.clients);
    } else {
      return reduxFacilities;
    }
  }

  buildFacilitiesListFromClientsList(clients) {
    let nameList = [];
    let list = [];
    clients.forEach((cl) => {
      const fac = cl.facilityName;
      if (fac && fac !== 'Unassigned' && nameList.indexOf(fac) < 0) {
        nameList = [...nameList, fac];
        const newFacility = {
          facilityId: 0,
          name: fac,
          facilityAddress: {},
          phoneNumbers: [],
        };
        list = [...list, newFacility];
      }
    });
    this.store.dispatch(setEmrFacilities(list));
    return list;
  }

  getFirmById(firmId) {
    const state = this.store.getState();
    const retVal = state.firms.find((firm) => firm.firmId === firmId);
    if (!retVal) {
      console.log(`*** Couldn't find Firm with id: ${firmId} ***`);
    }
    return retVal;
  }

  getAllKnownFirms() {
    const myStore = this.store;
    return myStore ? myStore.getState().firmInfo.knownFirms : [];
  }

  getVisibleFirmInfo(firmId) {
    const state = this.store.getState();
    const retVal = state.firmInfo.knownFirms.find((firm) => firm.id === firmId);
    if (!retVal) {
      console.log(`*** Couldn't find Firm with id: ${firmId} ***`);
    }
    return retVal;
  }

  getCurrentCase() {
    const cases = this.store.getState().cases;
    const currentCaseId = cases.currentCaseId;
    const kase = cases.loaded.find((c) => c.caseId === currentCaseId);
    return kase ? this.resolveCaseDates(kase) : undefined;
  }

  resolveCaseDates(kase) {
    return {
      ...kase,
      creationDate: DateTime.fromISO(kase.creationDate),
      firstContactDate: DateTime.fromISO(kase.firstContactDate),
      admissionDate: DateTime.fromISO(kase.admissionDate),
      dischargeDate: DateTime.fromISO(kase.dischargeDate),
    };
  }

  getCaseIntendedClientId() {
    const state = this.store.getState();
    return state.intendedInfo.intendedClientId;
  }

  getIntendedAccountId() {
    const state = this.store.getState();
    return state.intendedInfo.intendedAccountId;
  }

  getIntendedReferrerCompanyId() {
    const state = this.store.getState();
    return state.intendedInfo.intendedReferrerCompanyId;
  }

  getIntendedReferrerContactId() {
    const state = this.store.getState();
    return state.intendedInfo.intendedReferrerContactId;
  }

  getEmergencyContactee() {
    const state = this.store.getState();
    return state.intendedInfo.intendedClientId;
  }

  getIntendedEmergencyContactId() {
    const state = this.store.getState();
    return state.intendedInfo.intendedEmergencyContactId;
  }

  getIntendedReferralId() {
    const state = this.store.getState();
    return state.intendedInfo.intendedReferralId;
  }

  makeCurrentCaseForClientWithId(clientId) {
    const state = this.store.getState();
    const desiredCurrentCase = state.cases.loaded.find((kase) => kase.clientId === clientId);
    this.store.dispatch(setCurrentCase(desiredCurrentCase));
  }

  haveLoadedCaseForClientWithId(clientId) {
    const state = this.store.getState();
    const matchingCase = state.cases.loaded.find((kase) => kase.clientId === clientId);
    return !!matchingCase;
  }

  haveContactsForClientWithId(clientId) {
    const state = this.store.getState();
    return state.emergencyContacts[clientId];
  }

  getContactsForClient(client) {
    const state = this.store.getState();
    const contacts = state.emergencyContacts[client.personId];
    return contacts || [];
  }

  getClientContactRelationship(clientId, contactId) {
    const state = this.store.getState();
    const idRelationshipPairs = state.emergencyContacts[clientId];
    const idRelationshipPair = idRelationshipPairs.find((pair) => pair.personId === contactId);
    return idRelationshipPair ? idRelationshipPair.relationship : '';
  }

  getClientContactDescription(clientId, contactId) {
    const state = this.store.getState();
    const contactInfos = state.emergencyContacts[clientId];
    const contactInfo = contactInfos.find((info) => info.personId === contactId);
    return contactInfo ? contactInfo.description : '';
  }

  getClientContactInactivity(clientId, contactId) {
    const state = this.store.getState();
    const contactInfos = state.emergencyContacts[clientId];
    const contactInfo = contactInfos.find((info) => info.personId === contactId);
    return contactInfo ? contactInfo.inactive : false;
  }

  getIdsOfClientsWithEmergencyContactWithId(contactId) {
    const state = this.store.getState();
    const emergencyContacts = state.emergencyContacts;
    for (const key of Object.keys(emergencyContacts)) {
      const contactPairs = emergencyContacts[key];
      const haveMatchingPair = contactPairs.some((pair) => pair.personId === contactId);
      if (haveMatchingPair) {
        return [parseInt(key)];
      }
    }
    return [];
  }

  getRelationshipOfContactToClient(contactId, clientId) {
    const state = this.store.getState();
    const contacts = state.emergencyContacts[clientId];
    if (!contacts || contacts.length === 0) {
      return null;
    }
    const relevantContact = contacts.find((cc) => cc.personId === contactId);
    return relevantContact ? relevantContact.relationship : null;
  }

  haveNotesOnPersonWithId(personId) {
    const state = this.store.getState();
    return state.notes.onPersons.some((note) => note.targetId === personId);
  }

  getNoteWithId(id) {
    const state = this.store.getState();
    const allNotes = state.notes.all;
    const note = allNotes.find((note) => note.noteId === id);
    return this.resolveNoteDates(note);
  }

  resolveNoteDates(note) {
    return {
      ...note,
      date: DateTime.fromISO(note.date),
    };
  }

  getNotesOnPerson(person) {
    const state = this.store.getState();
    const matchingNotes = state.notes.onPersons.filter(
      (n) => n.targetId === person.personId || n.subjectId === person.personId
    );
    return matchingNotes.map((note) => this.resolveNoteDates(note));
  }

  getReferralsFromContact(personId) {
    const referrals = this.store.getState().referrals;
    const matchingReferrals = referrals.filter((referral) => referral.referrerId === personId);
    return matchingReferrals.map((r) => this.resolveReferralDates(r));
  }

  getThinReferralsFromContact(personId) {
    const referrals = this.store.getState().thinReferrals;
    const matchingReferrals = referrals.filter((referral) => referral.referrerId === personId);
    return matchingReferrals.map((r) => this.resolveReferralDates(r));
  }

  resolveReferralDates(referral) {
    if (referral.contactDate && !referral.date) {
      referral = {
        ...referral,
        date: referral.contactDate,
      };
    }
    if (referral.date && !referral.contactDate) {
      referral = {
        ...referral,
        contactDate: referral.date,
      };
    }
    return {
      ...referral,
      date: DateTime.fromISO(referral.date),
      contactDate: referral.contactDate ? DateTime.fromISO(referral.contactDate) : undefined,
    };
  }

  haveNotesOnFirmWithId(firmId) {
    const state = this.store.getState();
    return state.notes.onFirms.some((note) => note.otherFirmId === firmId);
  }

  haveNotesOnReferralWithId(referralId) {
    const state = this.store.getState();
    return state.notes.onReferrals.some((note) => note.referralId === referralId);
  }

  getNotesAboutFirmWithId(firmId) {
    const state = this.store.getState();
    const matchingNotes = state.notes.onFirms.filter((note) => note.otherFirmId === firmId);
    return matchingNotes.map((n) => this.resolveNoteDates(n));
  }

  getNotesAboutReferal(referralId) {
    const state = this.store.getState();
    const matchingNotes = state.notes.onReferrals.filter((note) => note.referralId === referralId);
    return matchingNotes.map((n) => this.resolveNoteDates(n));
  }

  getNotesAboutInquiryWithId(inquiryId) {
    const state = this.store.getState();
    const matchingNotes = state.notes.onInquiries.filter((note) => note.inquiryId === inquiryId);
    return matchingNotes.map((n) => this.resolveNoteDates(n));
  }

  getDocumentsAboutInquiryWithId(inquiryId) {
    const state = this.store.getState();
    return state.documents.filter((doc) => doc.inquiryId === inquiryId);
  }

  getNotesAboutCallRailCallWithId(callRailCallId) {
    const state = this.store.getState();
    const matchingNotes = state.notes.onCallRailCalls.filter((note) => note.callRailCallId === callRailCallId);
    return matchingNotes.map((n) => this.resolveNoteDates(n));
  }

  getNoteAboutCallRailCallWithId(callRailCallId) {
    const notes = this.getNotesAboutCallRailCallWithId(callRailCallId);
    return notes.length > 0 ? notes[0] : null;
  }

  getDocumentsAboutPersonWithPersonId(personId) {
    const state = this.store.getState();
    return state.documents.filter((doc) => doc.personId === personId);
  }

  haveOutreachHistoryForPersonWithId(personId) {
    return false;
    /*
    const state = this.store.getState();
    return Object.keys(state.outreachLists).indexOf(personId.toString()) >= 0;
    */
  }

  haveOutreachHistoryWithFirm(visibleFirmId) {
    const reduxOutreaches = this.store.getState().outreaches;
    const knownOutreachesToFirm = reduxOutreaches.toFirm[visibleFirmId] || [];
    if (knownOutreachesToFirm.length > 0) {
      return true;
    }
    const knownOutreachesFromFirm = reduxOutreaches.fromFirm[visibleFirmId] || [];
    return knownOutreachesFromFirm.length > 0;
  }

  getOutreachHistoryForClientWithId(clientId) {
    let outreaches = this.getDirectOutreachHistoryForPersonWithId(clientId);
    const client = this.getPersonById(clientId);
    const contacts = this.getContactsForClient(client);
    contacts.forEach((contact) => {
      outreaches = outreaches.concat(this.getDirectOutreachHistoryForPersonWithId(contact.personId));
    });
    return outreaches.map((o) => this.resolveOutreachDates(o));
  }

  resolveOutreachDates(outreach) {
    return {
      ...outreach,
      date: DateTime.fromISO(outreach.date),
    };
  }

  getDirectOutreachHistoryForPersonWithId(personId) {
    const state = this.store.getState();
    return state.outreachLists[personId] || [];
  }

  getOutreachById(outreachId) {
    const outreachState = this.store.getState().outreaches;
    const category = outreachState.outreachToCategoryMap[outreachId];
    const key = outreachState.outreachToKeyMap[outreachId];
    const outreachesToSameEntity = outreachState[category][key];
    const matchingOutreaches = outreachesToSameEntity.find((o) => o.outreachId === outreachId);
    return matchingOutreaches.map((o) => this.resolveOutreachDates(o));
  }

  getOutreachByIds(personId, outreachId) {
    const outreaches = this.getDirectOutreachHistoryForPersonWithId(personId);
    const matchingOutreach = outreaches.find((or) => or.outreachId === outreachId);
    const resolvedOutreach = this.resolveOutreachDates(matchingOutreach);
    return resolvedOutreach;
  }

  getOutreachFromById(outreachId, personId) {
    const outreachLists = this.store.getState().outreachLists;
    const keys = Object.keys(outreachLists);
    const lists = keys.map((key) => outreachLists[key]);
    let retVal = null;
    lists.forEach((list) => {
      const match = list.find((outreach) => outreach.outreachId === outreachId && outreach.senderId === personId);
      if (match) {
        retVal = match;
      }
    });
    return retVal;
  }

  getSenderFirmOfOutreachWithCallRailCall(callId) {
    const missive = this.getMissiveWithCallRailCallId(callId);
    if (!missive) {
      return null;
    }
    const firmId = this.getFirmIdWithFromFirmOutreachWithMissive(missive.missiveId);
    if (firmId === 0) {
      return null;
    } else {
      return this.getFirmById(firmId);
    }
    /*
    const outreach = this.getFromFirmOutreachWithMissive(missive.missiveId);
    if (!outreach) {
      return null;
    }
    const senderFirmId = outreach.senderFirmId;
    return this.getFirmById(senderFirmId);
    */
  }

  getMissiveWithCallRailCallId(callId) {
    const missives = this.store.getState().missives;
    return missives.find((m) => m.callRailCallId === callId);
  }

  getFirmIdWithFromFirmOutreachWithMissive(missiveId) {
    const categorizedOutreaches = this.store.getState().outreaches;
    const fromFirmOutreaches = categorizedOutreaches.fromFirm;
    const keys = Object.keys(fromFirmOutreaches);
    let firmId = 0;
    keys.forEach((key) => {
      const listForFirm = fromFirmOutreaches[key];
      const foundOutreach = listForFirm.find((o) => o.missiveId === missiveId);
      if (foundOutreach) {
        firmId = foundOutreach.senderFirmId;
      }
    });
    return firmId;
  }

  getDescripForOutreach(outreach) {
    const missiveId = outreach.missiveId;
    const missive = this.getMissiveById(missiveId);
    return missive ? missive.name : 'Unnamed Missive';
  }

  isFirmOutreach(outreachId) {
    const category = this.getFirmOutreachCategory(outreachId);
    return category === 'toFirmContact' || category === 'fromFirmContact';
  }

  getFirmOutreachCategory(outreachId) {
    const state = this.store.getState();
    const outreachState = state.outreaches;
    return outreachState.outreachToCategoryMap[outreachId];
  }

  getMissiveById(missiveId) {
    const state = this.store.getState();
    return state.missives.find((missive) => missive.missiveId === missiveId);
  }

  getDistributionListById(dlId) {
    const state = this.store.getState();
    return state.distributionLists.find((dl) => dl.distributionListId === dlId);
  }

  getMostRecentOutreachForPersonWithId(personId) {
    const outreaches = this.getOutreachHistoryForClientWithId(personId);
    if (!outreaches || outreaches.length === 0) {
      return undefined;
    }
    let latestOutreach = outreaches[0];
    let latestTimeSoFar = latestOutreach.date;
    for (let i = 1; i < outreaches.length; i++) {
      const newTime = outreaches[i].date;
      if (newTime > latestTimeSoFar) {
        latestOutreach = outreaches[i];
        latestTimeSoFar = latestOutreach.date;
      }
    }
    return this.resolveOutreachDates(latestOutreach);
  }

  getIdsOfFirmsVisibleToFirm(viewingFirmId) {
    const state = this.store.getState();
    if (!state.orgContacts) {
      return [];
    }

    return state.orgContacts.map((struct) => struct.referralFirmId);
  }

  getIdsOfContactsAtVisibleFirm(visibleFirmId) {
    const state = this.store.getState();
    if (!state.orgContacts) {
      return [];
    }
    const structForVisibleFirm = state.orgContacts.find((struct) => struct.referralFirmId === visibleFirmId);
    if (!structForVisibleFirm) {
      return [];
    } else {
      return structForVisibleFirm.contactIds;
    }
  }

  getContactsAtVisibleFirm(visibleFirmId) {
    if (visibleFirmId > 0) {
      const contactIds = this.getIdsOfContactsAtVisibleFirm(visibleFirmId);
      return contactIds.map((contactId) => this.getPersonById(contactId));
    } else {
      const idNamePairs = this.getUnaffiliatedContacts();
      return idNamePairs.map((pair) => this.getPersonById(pair.id));
    }
  }

  getReferralsFromFirm(visibleFirmId) {
    const visibleFirm = this.getFirmById(visibleFirmId);
    const referralIds = visibleFirm.referralFromIds;
    const allReferrals = this.store.getState().referrals;
    // Do this a more complicated way because a referral may have been deleted
    // const matchingReferrals = referralIds.map(refId => allReferrals.find(referral => referral.referralId === refId));
    let matchingReferrals = [];
    referralIds.forEach((refId) => {
      const matchingReferral = allReferrals.find((referral) => referral.referralId === refId);
      if (matchingReferral) {
        matchingReferrals = [...matchingReferrals, matchingReferral];
      }
    });
    return matchingReferrals.map((r) => this.resolveReferralDates(r));
  }

  haveTaskInfoForUser(userPersonId) {
    const state = this.store.getState();
    const userTaskState = state.taskInfo;
    if (userTaskState) {
      return userTaskState.relevantUserPersonId === userPersonId;
    } else {
      return false;
    }
  }

  getIdsOfClientsOfFirm(firmId) {
    const state = this.store.getState();
    const firmClientsState = state.clientsInfo;
    if (firmClientsState.firmId === firmId) {
      return firmClientsState.clientIds;
    } else {
      return [];
    }
  }

  haveClientsInfoForFirm(firmId) {
    const state = this.store.getState();
    const firmClientsState = state.clientsInfo;
    if (firmClientsState) {
      return firmClientsState.firmId === firmId;
    } else {
      return false;
    }
  }

  getAllTasks() {
    const allTasks = this.store.getState().taskInfo.tasks;
    return allTasks.map((t) => this.resolveTaskDates(t));
  }

  getTaskById(taskId) {
    const state = this.store.getState();
    const matchingTask = state.taskInfo.tasks.find((task) => task.id === taskId);
    return this.resolveTaskDates(matchingTask);
  }

  getTasksRelatedToVisibleFirm(visibleFirmId) {
    const orgTasksState = this.store.getState().orgTasks;
    const matchingTasks = orgTasksState[visibleFirmId] || [];
    return matchingTasks.map((t) => this.resolveTaskDates(t));
  }

  farFutureDate = DateTime.fromFormat('5000', 'yyyy', {});

  resolveTaskDates(task) {
    return {
      ...task,
      activationDate: DateTime.fromISO(task.activationDate),
      nextDeadline: task.nextDeadline ? DateTime.fromISO(task.nextDeadline) : this.farFutureDate,
      assignmentDeadline: DateTime.fromISO(task.assignmentDeadline),
      acceptanceDeadline: DateTime.fromISO(task.acceptanceDeadline),
      completionDeadline: DateTime.fromISO(task.completionDeadline),
      completionTime: task.completionTime ? DateTime.fromISO(task.completionTime) : this.farFutureDate,
    };
  }

  getTasksRelatedToClient(clientId) {
    let relatedTasks = this.getTasksWithSubject(clientId);
    const state = this.store.getState();
    const contacts = state.emergencyContacts[clientId] || [];
    contacts.forEach((c) => (relatedTasks = [...relatedTasks, ...this.getTasksWithSubject(c.personId)]));
    return relatedTasks.map((t) => this.resolveTaskDates(t));
  }

  getTasksWithSubject(personId) {
    const tasks = this.store.getState().taskInfo.tasks;
    const matchingTasks = tasks.filter((t) => t.subjectId === personId);
    return matchingTasks.map((t) => this.resolveTaskDates(t));
  }

  getTaskableGroupById(groupId) {
    const state = this.store.getState();
    const groups = state.taskInfo.taskableGroups;
    return groups.find((g) => g.id === groupId);
  }

  haveListOfTasksRelatedToVisibleFirm(viewingFirmId, visibleFirmId) {
    const state = this.store.getState();
    if (!(state.orgTasks && state.orgTasks instanceof Map)) {
      return false;
    }
    const mapForViewingFirm = state.orgTasks.get(viewingFirmId);
    if (!mapForViewingFirm) {
      return false;
    }
    const tasksForVisibleFirm = mapForViewingFirm.get(visibleFirmId);
    return !!tasksForVisibleFirm;
  }

  haveTaskWithId(taskId) {
    const state = this.store.getState();
    return state.taskInfo.tasks.some((t) => t.id === taskId);
  }

  haveTaskableGroupWithId(groupId) {
    const state = this.store.getState();
    return state.taskInfo.taskableGroups.some((tg) => tg.id === groupId);
  }

  getOutreachesRelatedToVisibleFirm(visibleFirmId) {
    const state = this.store.getState();
    const outreachesState = state.outreaches;
    const toFirmMap = outreachesState.toFirm;
    const fromFirmMap = outreachesState.fromFirm;
    const toContactMap = outreachesState.toFirmContact;
    const fromContactMap = outreachesState.fromFirmContact;
    let outreaches = [];
    const outreachesToFirm = toFirmMap[visibleFirmId] || [];
    const outreachesFromFirm = fromFirmMap[visibleFirmId] || [];
    outreaches = [...outreaches, ...outreachesToFirm, ...outreachesFromFirm];
    const contacts = this.getContactsAtVisibleFirm(visibleFirmId);
    contacts.forEach((ctct) => {
      const outreachesToThisContact = toContactMap[ctct.personId] || [];
      const outreachesFromThisContact = fromContactMap[ctct.personId] || [];
      outreaches = [...outreaches, ...outreachesToThisContact, ...outreachesFromThisContact];
    });
    return outreaches.map((o) => this.resolveOutreachDates(o));
  }

  getFacilitiesForFirm(firmId) {
    const state = this.store.getState();
    const chosenFirm = state.firms.find((f) => f.firmId === firmId);
    return chosenFirm.facilities;
  }

  getStatusHistorysForFirmIdAndClientId(firmId, clientId) {
    const state = this.store.getState();
    const matching = state.statusTransitions.filter((st) => st.subjectId === clientId && st.firmId === firmId);
    return matching.map((st) => this.resolveStatusTransitionDates(st));
  }

  resolveStatusTransitionDates(statusTransition) {
    return {
      ...statusTransition,
      date: DateTime.fromISO(statusTransition.date),
    };
  }

  getReferralOfPersonToFirm(personId, firmId) {
    const state = this.store.getState();
    const matchingReferral = state.referrals.find(
      (ref) => ref.referreeId === personId && ref.receivingOrgId === firmId
    );
    return matchingReferral ? this.resolveReferralDates(matchingReferral) : matchingReferral;
  }

  getReferralById(referralId) {
    const referrals = this.store.getState().referrals;
    const matchingReferral = referrals.find((ref) => ref.referralId === referralId);
    return this.resolveReferralDates(matchingReferral);
  }

  getAllInquiries() {
    const allInquiries = this.store.getState().inquiries;
    return allInquiries.map((inquiry) => this.resolveInquiryDates(inquiry));
  }

  getInquiryById(id) {
    const allInquiries = this.store.getState().inquiries;
    const inquiry = allInquiries.find((i) => i.inquiryId === id);
    return this.resolveInquiryDates(inquiry);
  }

  resolveInquiryDates(inquiry) {
    return {
      ...inquiry,
      date: DateTime.fromISO(inquiry.date),
    };
  }

  getDocumentById(id) {
    const state = this.store.getState();
    return state.documents.find((d) => d.storedDocumentId === id);
  }

  getInsuranceInfoById(id) {
    const state = this.store.getState();
    return state.insuranceInfos.find((iInfo) => iInfo.insuranceInfoId === id);
  }

  getTreatmentProgramById(id) {
    const state = this.store.getState();
    return state.treatmentPrograms.find((program) => program.treatmentProgramId === id);
  }

  getStatusOfClientWithId(clientId, firmId) {
    const state = this.store.getState();
    const transitionsForClient = state.statusTransitions.filter(
      (st) => st.subjectId === clientId && st.firmId === firmId
    );
    if (transitionsForClient.length === 0) {
      return 'None';
    }
    const lastTransition = transitionsForClient[transitionsForClient.length - 1];
    return lastTransition.newStatus;
  }

  getMobileNumberForPerson(person) {
    const phonesArr = person.phoneNumbers;
    if (!phonesArr || phonesArr.length === 0) {
      return 'Unknown';
    }
    const mobileArr = phonesArr.filter((rec) => rec.roles && rec.roles.indexOf('Mobile') >= 0).map((rec) => rec.number);
    return mobileArr.length === 0 ? 'Unknown' : mobileArr[0];
  }

  getPhoneNumberForPerson(person) {
    const mobileNumber = this.getMobileNumberForPerson(person);
    if (mobileNumber === 'Unknown') {
      const phonesArr = person.phoneNumbers;
      if (!phonesArr || phonesArr.length === 0) {
        return 'Unknown';
      }
      const phoneRec = phonesArr[0];
      return phoneRec.number;
    } else {
      return mobileNumber;
    }
  }

  /*
  gitInitialCallFormPrimingInfo() {
    const state = this.store.getState();
    return state.intendedInfo.gitInitialCallFormPrimingInfo;
  }
  */

  getVobFormInfosForPersonWithId(personId) {
    const state = this.store.getState();
    const infosForPerson = state.vobFormInfos[personId] || [];
    // At this point, 'verificationDate,' 'birthDate,' 'effectiveDate,' 'renewalDate,' and 'lastCobUpdated'
    //  are all strings or null
    // We want them to be Luxon DateTimes so interaction code is natural. So:
    return infosForPerson.map((info) => this.resolveVobFormInfoDates(info));
  }

  resolveVobFormInfoDates(info) {
    const verificationDate = this.resolveVobDateIfGiven(info, 'verificationDate');
    const birthDate = this.resolveVobDateIfGiven(info, 'birthDate');
    const effectiveDate = this.resolveVobDateIfGiven(info, 'effectiveDate');
    const renewalDate = this.resolveVobDateIfGiven(info, 'renewalDate');
    const lastCobUpdated = this.resolveVobDateIfGiven(info, 'lastCobUpdated');
    return {
      ...info,
      verificationDate,
      birthDate,
      effectiveDate,
      renewalDate,
      lastCobUpdated,
    };
  }

  resolveVobDateIfGiven(info, fieldName) {
    return info[fieldName] ? DateTime.fromISO(info[fieldName]) : null;
  }

  getLastRequestedTasksAccomplishedTimeRange() {
    const state = this.store.getState();
    const { firstDate: firstDateStr, lastDate: lastDateStr } = state.dashboardStats.taskAccomplishmentInfo;
    const firstDate = firstDateStr ? DateTime.fromISO(firstDateStr) : null;
    const lastDate = lastDateStr ? DateTime.fromISO(lastDateStr) : null;
    return { firstDate, lastDate };
  }

  /*
   * Users want to see this changing SOON after doing tasks, so don't use caching
  getCachedTasksAccomplishedResultsForTimeRange(timeRange) {
    const state = this.store.getState();
    const taskAccomplishmentInfo = state.dashboardStats.taskAccomplishmentInfo;
    const objectForRange = taskAccomplishmentInfo.rangesAndResults.find((rnr) => rnr.timeRange === timeRange);
    if (!objectForRange) {
      return objectForRange; // i.e. undefined
    }
    const cacheTime = DateTime.fromISO(objectForRange.cacheTime);
    const dT = DateTime.local().diff(cacheTime);
    return dT.as('minutes') < 1 ? objectForRange.results : undefined;
  }
  */

  /*
  getLastRequestedTopReferrersTimeRange() {
    const state = this.store.getState();
    const topReferrersInfo = state.dashboardStats.topReferrersInfo;
    return topReferrersInfo.lastTimeRange;
  }
  */

  getLastRequestedTopReferrersTimeRange() {
    const state = this.store.getState();
    const { firstDate: firstDateStr, lastDate: lastDateStr } = state.dashboardStats.topReferrersInfo;
    const firstDate = firstDateStr ? DateTime.fromISO(firstDateStr) : null;
    const lastDate = lastDateStr ? DateTime.fromISO(lastDateStr) : null;
    return { firstDate, lastDate };
  }

  getLastRequestedTopReferrersMaxNToShow() {
    const state = this.store.getState();
    const topReferrersInfo = state.dashboardStats.topReferrersInfo;
    return topReferrersInfo.maxNToShow;
  }

  /*
  getCachedTopReferrersResultsForTimeRange(timeRange) {
    const state = this.store.getState();
    const topReferrersInfo = state.dashboardStats.topReferrersInfo;
    const objectForRange = topReferrersInfo.rangesAndResults.find((rnr) => rnr.timeRange === timeRange);
    if (!objectForRange) {
      return objectForRange;
    }
    const cacheTime = DateTime.fromISO(objectForRange.cacheTime);
    const dT = DateTime.local().diff(cacheTime);
    return dT.as('minutes') < 1 ? objectForRange.results : undefined;
  }
  */

  getAllDropdowns() {
    if (!this.store) {
      return [];
    }
    const firmInfoState = this.store.getState().firmInfo;
    return firmInfoState.dropdowns;
  }

  getCallRailCall(callId) {
    const allCalls = this.store.getState().callRailCalls.allCalls;
    const matchingCall = allCalls.find((call) => call.callRailCallId === callId);
    return matchingCall ? this.resolveCallRailCallDates(matchingCall) : matchingCall;
  }

  resolveCallRailCallDates(call) {
    return {
      ...call,
      date: call.date ? DateTime.fromISO(call.date) : call.date,
    };
  }
}

export default new ReduxAccess();
